// Live client-side scan for restricted info in an outbound eBay reply — a
// proactive warning ahead of Send, catching what server/src/ai/redaction.js's
// PII redaction pass (server-side, pre-LLM) is not in this path to catch: the
// CSR's own edits to a draft, or a manually-typed reply. Mirrors (does not
// import — that's server code) redaction.js's EMAIL/PHONE/CARD patterns,
// including the CARD pattern's Luhn-checksum validation, so a 15-16 digit
// order/tracking number in a reply isn't false-flagged as a credit card —
// the same false-positive risk redaction.js's luhnValid() guards against.

// Luhn checksum over a digit string — see redaction.js's luhnValid() for why
// this exists (a 15-16 digit tracking/order number shape-matches the CARD
// regex but fails Luhn almost every time).
function luhnValid(digits) {
  if (digits.length < 13 || digits.length > 16) return false
  let sum = 0
  let dbl = false
  for (let i = digits.length - 1; i >= 0; i--) {
    let d = digits.charCodeAt(i) - 48
    if (dbl) { d *= 2; if (d > 9) d -= 9 }
    sum += d
    dbl = !dbl
  }
  return sum % 10 === 0
}

const PATTERNS = [
  { type: 'EMAIL', re: /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi },
  { type: 'CARD', re: /\b(?:\d[ -]?){13,16}\b/g, validate: (m) => luhnValid(m.replace(/[ -]/g, '')) },
  { type: 'PHONE', re: /(?:\+?1[\s.-]?)?(?:\(\d{3}\)|\d{3})[\s.-]\d{3}[\s.-]\d{4}\b/g },
]

// Returns the subset of ['EMAIL', 'PHONE', 'CARD'] found in text, in that
// fixed order regardless of where each match occurs.
export function findRestrictedInfo(text) {
  if (!text) return []
  const found = []
  for (const { type, re, validate } of PATTERNS) {
    re.lastIndex = 0
    const match = text.match(re)
    if (match && (!validate || match.some((m) => validate(m)))) {
      found.push(type)
    }
  }
  return found
}

const LABELS = { EMAIL: 'an email address', PHONE: 'a phone number', CARD: 'a credit card number' }

// Joins found types into the warning's natural-language list ("an email
// address and a phone number") and wraps it in the full warning sentence.
// Returns null when nothing was found (the render-gate case).
export function restrictedInfoWarning(text) {
  const found = findRestrictedInfo(text)
  if (found.length === 0) return null
  const labels = found.map((t) => LABELS[t])
  const joined = labels.length === 1
    ? labels[0]
    : `${labels.slice(0, -1).join(', ')}${labels.length > 2 ? ',' : ''} and ${labels[labels.length - 1]}`
  return `Warning: Make sure you have not included ${joined} in your response as this is against eBay guidelines.`
}
